import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useRef, useEffect } from 'react';

import Box from '@mui/material/Box';
import { iconButtonClasses } from '@mui/material/IconButton';
import { useTheme, useColorScheme } from '@mui/material/styles';

import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';

import { _account } from '../nav-config-account';
import { Searchbar } from '../components/searchbar';
import { AccountDrawer } from '../components/account-drawer';
import { MainSection, HeaderSection, LayoutSection } from '../core';
import { NotificationsDrawer } from '../components/notifications-drawer';

// ----------------------------------------------------------------------

export type MvpLayoutProps = {
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  layoutQuery?: Breakpoint;
};

export function MvpLayout({ sx, children, layoutQuery = 'lg' }: MvpLayoutProps) {
  const theme = useTheme();
  const { setMode } = useColorScheme();
  const settings = useSettingsContext();
  const hasSetDarkMode = useRef(false);

  // Set dark mode as default for MVP page (only once on mount)
  useEffect(() => {
    if (!hasSetDarkMode.current) {
      hasSetDarkMode.current = true;
      setMode('dark');
      settings.setState({ mode: 'dark' });
    }
  }, [setMode, settings]);

  const renderHeader = () => {
    const headerSlotProps = {
      container: {
        maxWidth: false as const,
        sx: {
          px: { [layoutQuery]: 5 },
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          [`& .${iconButtonClasses.root}`]: {
            color: '#FFFFFF',
          },
        },
      },
    };

    const headerSlots = {
      leftArea: (
        <Logo
          sx={{
            display: 'inline-flex',
          }}
        />
      ),
      rightArea: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0, sm: 0.75 },
          }}
        >
          <Searchbar data={[]} />
          <NotificationsDrawer />
          <AccountDrawer data={_account} />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation
        slots={headerSlots}
        slotProps={headerSlotProps}
      />
    );
  };

  const renderMain = () => <MainSection>{children}</MainSection>;

  return (
    <LayoutSection
      headerSection={renderHeader()}
      sx={[
        {
          minHeight: '100vh',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
